import React, { useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import StyledTableRow from 'shared/components/table/StyledTableRow';
import Paper from '@mui/material/Paper';
import ExpandIcon from '@mui/icons-material/Expand';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import Collapse from '@mui/material/Collapse';
import { dateTimeString } from 'shared/utils';
import { SUPPORT_SOURCES } from 'shared/constants';
import { useAuth, useRequest } from 'shared/hooks';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import { EditEntryDialog } from './EntryDialog';

export const useHasOverflow = (ref, text) => {
  const [overflow, setOverflow] = useState(undefined);

  useEffect(() => {
    const { current } = ref;

    const trigger = () => {
      const hasOverflow = current.scrollHeight > current.clientHeight;
      setOverflow(hasOverflow);
    };

    if (current) {
      trigger();
    }
  }, [ref, text]);

  return overflow;
};

/**
 * @param {Object} props
 * @param {string} props.entryText
 * @param {boolean} props.firstEntry
 */
function EntryTextWrapper({ entryText, firstEntry }) {
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const overflow = useHasOverflow(ref, entryText);

  useEffect(() => {
    setOpen(overflow && firstEntry);
  }, [overflow, firstEntry]);

  function expandCollapseIcon() {
    return (
      open
        ? (
          <TooltipIconButton
            className="entry-icon"
            message="Collapse text"
            icon={UnfoldLessIcon}
            onClick={() => setOpen(!open)}
          />
        )
        : (
          <TooltipIconButton
            className="entry-icon"
            message="Expand text"
            icon={ExpandIcon}
            onClick={() => setOpen(!open)}
          />
        )
    );
  }

  return (
    <>
      <TableCell style={{ width: '3%', paddingRight: 0, verticalAlign: 'top' }}>
        {overflow && expandCollapseIcon()}
      </TableCell>
      <TableCell style={{ verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {!open
          ? (
            <div className={open ? '' : 'line-clamp'} ref={ref}>
              {entryText}
            </div>
          )
          : (
            <Collapse in={open} timeout="auto" unmountOnExit style={{ paddingBottom: 10, paddingTop: 10 }}>
              {entryText}
            </Collapse>
          )}
      </TableCell>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.entry
 * @param {boolean} props.firstEntry
 * @param {boolean} props.inDialog
 * @param {Function} props.reload
 * @param {number} props.assigneeId
 * @param {boolean} props.isManager
 */
function Row(props) {
  const { user } = useAuth();
  const {
    entry,
    firstEntry,
    inDialog,
    reload,
    assigneeId,
    isManager,
  } = props;

  const {
    minutes,
    username,
    created_at: createdAt,
  } = entry;

  const editExpiryTime = createdAt && new Date(new Date(createdAt).getTime() + 600000);
  const [canEdit, setCanEdit] = useState((username === user.username) && Math.floor(
    (new Date() - new Date(createdAt)) / 60000,
  ) < 10 && minutes > 0);
  const systemEntry = entry.generated_by === SUPPORT_SOURCES.system;

  useEffect(() => {
    const elapsedTime = Math.floor((new Date() - new Date(createdAt)) / 60000);
    if (!canEdit || elapsedTime > 10) return undefined;
    const timer = setTimeout(() => setCanEdit(false), (10 - elapsedTime) * 60000);
    return () => clearTimeout(timer);
  }, [canEdit, createdAt]);

  return (
    <StyledTableRow>
      <EntryTextWrapper entryText={entry.text} firstEntry={firstEntry} />
      <TableCell style={{ width: '5%', verticalAlign: 'top' }}>{entry.minutes}</TableCell>
      <TableCell style={{ width: '5%', verticalAlign: 'top' }}>{entry.username}</TableCell>
      <TableCell style={{ width: '175px', verticalAlign: 'top' }}>
        {dateTimeString(entry.created_at)}
      </TableCell>
      <TableCell sx={{ p: '0 16px' }}>
        { ((!inDialog && canEdit) || isManager) && !systemEntry && (
          <EditEntryDialog
            entry={entry}
            expireTime={dateTimeString(editExpiryTime)}
            reload={reload}
            employeeId={assigneeId}
            managerId={user.id}
          />
        )}
      </TableCell>
    </StyledTableRow>
  );
}

const entriesColumns = [
  {
    name: 'Text',
    jsonKey: 'text',
  },
  {
    name: 'Minutes',
    jsonKey: 'minutes',
  },
  {
    name: 'Created By',
    jsonKey: 'username',
  },
  {
    name: 'Created At',
    jsonKey: 'created_at',
  },
];

/**
 * @param {Object} props
 * @param {string} [props.className='default-table-height']
 * @param {Array<*>} [props.entries=[]]
 * @param {boolean} [props.inDialog=false]
 * @param {Function} props.reload
 * @param {number} props.assigneeId
 * @param {boolean} props.isManager
 */
export default function CollapsibleEntryTable(props) {
  const { isLoading } = useRequest();
  const {
    className = 'default-table-height',
    inDialog = false,
    entries = [],
    reload,
    assigneeId,
    isManager,
  } = props;

  const firstEntry = entries.find((entry) => entry.username !== 'System');

  return (
    !entries || entries.length === 0
      ? (
        <strong>{isLoading ? 'Loading...' : 'No Ticket Entries'}</strong>
      )
      : (
        <TableContainer className={className} component={Paper}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell size="small" style={{ width: 35, padding: 10 }} />
                {entriesColumns.map(({ name, jsonKey }) => (
                  <TableCell className="row-font" key={jsonKey}>{name}</TableCell>
                ))}
                <TableCell size="small" style={{ width: 35, padding: 10 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <Row
                  key={entry.id}
                  entry={entry}
                  firstEntry={inDialog || entry === firstEntry}
                  reload={reload}
                  inDialog={inDialog}
                  assigneeId={assigneeId}
                  isManager={isManager}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
  );
}
